import { useCallback } from 'react'
import { ButtonOrLoader } from '../../../../../../../../../../../baseComponents/Button'
import LoadingSpinner from '../../../../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { ModalComponentPropsWithoutSubmit } from '../../../../../../../../../../../baseComponents/ModalsLayouts/types'
import { RowData } from '../../../../../../../../../../../baseComponents/TwinTable/types'
import VirtualTableListing from '../../../../../../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { CustomRenderCountryName, CustomRenderStateName, DeleteRow } from '../../../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { getCountryNameById, getStateNameById } from '../../../../../../../../../../../utils/globals/countries'
import twinFetchPost from '../../../../../../../../../../../utils/globals/data'
import useIsLoading from '../../../../../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage, changeSuccessMessage } from '../../../../../../../../../../../utils/reducers/reduxDispatch'
import { ShippingZoneGroupModelType } from '@teinor/erp/types/company/config/shipping/shippingZoneGroup'
import { ShippingZoneModelType } from '@teinor/erp/types/company/config/shipping/shippingZoneGroup/shippingZone'

interface ModalCEShippingTableProps extends ModalCEShippingTableLogicProps {
    allRowData: RowData
}

const ModalCEShippingTable: React.FC<ModalCEShippingTableProps> = ({ data, allRowData, ...logic }) => {
    const { t } = useTwinTranslation()
    const { deleteRow, loading, handleOnSubmit } = useModalCEShippingTableLogic({ data, allRowData,  ...logic })
    const columns: ColumnTableSchema<ShippingZoneModelType > = [
        {
            id: 'country',
            dataKey: 'country',
            label: t('country', 'País'),
            customRender: (parameterValue) => <CustomRenderCountryName countryId={parameterValue} />
        },
        {
            id: 'state',
            dataKey: 'state',
            label: t('state', 'Provincia'),
            customRender: (parameterValue, allRowData) => <CustomRenderStateName stateId={parameterValue} allRowData={allRowData} />
        },
        {
            id: 'postal_code',
            dataKey: 'postal_code',
            label: t('postalCode', 'Código postal'),
        },
        {
            id: 'delete',
            dataKey: 'postal_code',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData.rowIndex)} />,
        }
    ]
    if (!data) {
        return <LoadingSpinner />
    }
    const buttonText = allRowData && allRowData.id ? t('save', 'Guardar') : t('create', 'Crear') 
    return (
        <div className='flex-auto flex flex-col'>
            <div className='flex-auto table_container mt-20'>
                <VirtualTableListing tableData={data?.ShippingZones || []} columns={columns} name='modalCreateEditShippingZonesService' />
            </div>
            <div className='flex justify-end'>
                <ButtonOrLoader onClick={handleOnSubmit} buttonIsDisabled={loading} textButton={buttonText} className='mt-68' />
            </div>
        </div>
    )
}

interface ModalCEShippingTableLogicProps extends ModalComponentPropsWithoutSubmit {
    data: ShippingZoneGroupModelType
    setData: React.Dispatch<React.SetStateAction<ShippingZoneGroupModelType>>
    getAllData: () => Promise<void>
    allRowData: RowData
}

const useModalCEShippingTableLogic = ({ data, setData, setOpened, getAllData, allRowData }: ModalCEShippingTableLogicProps) => {
    const { t } = useTwinTranslation()
    const { endLoading, loading, startLoading } = useIsLoading()
    const shippingZoneRepeatedWithZonesError = t('cannotAddRepeatedShippingZones', 'No se puede añadir zonas de servicios repetidas')
    const shippingZoneRequiredError = t('shippingZonesRequired', 'Debe añadir zonas de envio')
    const successMessage = t('successfullyCompleted', 'Realizado correctamente')

    const deleteRow = useCallback((rowIndex: number) => {
        setData((oldData) => {
            const copy = JSON.parse(JSON.stringify(oldData))
            copy.ShippingZones.splice(rowIndex, 1)
            return ({ ...copy })
        })
    }, [setData])
    
    const handleOnSubmit = useCallback(async () => {
        startLoading()
        let url = '/api/app/config/shipping/shippingZone/createShippingZoneAndGroup'
        if (allRowData && allRowData.id) {
            url = '/api/app/config/shipping/shippingZone/updateShippingZoneAndGroup'
        }
        const result: Response = await twinFetchPost(url, { ...data, ShippingZones: data?.ShippingZones })
        endLoading()
        if (result && result.status === 200) {
            getAllData() 
            setOpened(null)
            changeSuccessMessage(successMessage)
        } else {
            const responseJSON = await result.json()
            if (responseJSON.errors[0].msg.includes('Shipping zone already exists') && responseJSON.errors[0].msg.includes('---')) {
                const zones = responseJSON.errors[0].msg.split('---')[1]
                const [country, state, postal_code] = zones.split(';')
                const countryName = getCountryNameById(country).name
                const myState = country && state ?getStateNameById(country, parseInt(state)) : false
                const stateName = myState ? (' - ' + myState.name) : ''
                const postalCode = postal_code ? (' - ' + postal_code) : ''
                const errorMsg = countryName + stateName + postalCode
                changeErrorMessage(shippingZoneRepeatedWithZonesError + ': ' + errorMsg)
            } else if (responseJSON.errors[0].msg === 'Shipping zones are required') {
                changeErrorMessage(shippingZoneRequiredError)
            }
        }
    }, [startLoading, data, getAllData, endLoading, setOpened, allRowData, shippingZoneRepeatedWithZonesError, shippingZoneRequiredError, successMessage])


    return { deleteRow, loading, handleOnSubmit }
}

export default ModalCEShippingTable